/**
 * createImperativeDialog — factory for promise-returning modal dialogs.
 *
 * Every transient dialog in PoseRef (Confirm, StorageQuota, Recovery,
 * InvalidSceneObjects, ExportMesh, SaveOrDiscard) used to hand-roll the
 * same three pieces:
 *
 *   1. `let _setRequest = null` at module scope
 *   2. `showXxx(...)` → returns Promise, stashes resolve, calls setRequest
 *   3. A component that registers its setState in a useEffect and renders
 *      JSX when the request is non-null
 *
 * This factory encapsulates all of it. Call it with a `render` function
 * that takes `(args, close)` and returns the dialog body — the rest is
 * automatic.
 *
 * ## Void-returning dialogs
 *
 * Setter-style dialogs (StorageQuotaDialog, ExportMeshModal) use
 * `Result = void`. `show(args)` still returns `Promise<void>`; callers
 * can ignore it. `close()` may be called without arguments.
 *
 * ## Cleanup
 *
 * If the Host component unmounts with a request still pending, the
 * pending promise is left un-resolved (same semantics as the old
 * hand-rolled pattern — every existing call site assumes the dialog
 * lives for the lifetime of the app). The setter is unregistered on
 * unmount so subsequent `show()` calls no-op cleanly rather than
 * writing into a dead setState.
 */

import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

export interface ImperativeDialog<Args, Result> {
  /**
   * Open the dialog with the given args. Returns a Promise that resolves
   * when `close(result)` is called. Callers that don't care about the
   * result can ignore the returned promise.
   */
  show: (args: Args) => Promise<Result>;
  /** Host component — mount this once at the app root. */
  Host: () => ReactNode;
}

interface InternalRequest<Args, Result> {
  args: Args;
  resolve: (result: Result) => void;
}

interface CreateOptions<Args, Result> {
  /**
   * Render the dialog body. `close` resolves the outstanding promise
   * and unmounts the dialog.
   */
  render: (args: Args, close: (result: Result) => void) => ReactNode;
}

export function createImperativeDialog<Args, Result = void>(
  options: CreateOptions<Args, Result>,
): ImperativeDialog<Args, Result> {
  let _setRequest:
    | ((req: InternalRequest<Args, Result> | null) => void)
    | null = null;

  const show = (args: Args): Promise<Result> =>
    new Promise<Result>((resolve) => {
      _setRequest?.({ args, resolve });
    });

  const Host = () => {
    const [request, setRequest] = useState<InternalRequest<
      Args,
      Result
    > | null>(null);

    // Keep resolve reachable from close() without stale-closure hazards:
    // close() needs the CURRENT request, even if multiple shows happen
    // in quick succession.
    const requestRef = useRef(request);
    requestRef.current = request;

    useEffect(() => {
      _setRequest = setRequest;
      return () => {
        _setRequest = null;
      };
    }, []);

    const close = useCallback((result: Result) => {
      const pending = requestRef.current?.resolve;
      setRequest(null);
      pending?.(result);
    }, []);

    if (!request) return null;
    return <>{options.render(request.args, close)}</>;
  };

  return { show, Host };
}
